import { Button } from "@mui/material";
import React, { useMemo, useState } from "react";
import { HelpDialog } from "./HelpDialog";
import { Form } from "react-final-form";
import setFieldData from "final-form-set-field-data";
import { Text } from "../../../common/components/input/Text";
import { Checkbox } from "../../../common/components/input/Checkbox";
import { MultiSelectChips, Select } from "../../../common/components/input/Select";
import { requiredNumber, requiredString, requiredUrl } from "../../util/validation";
import {
  CookieCategory,
  CustomCookieDto,
  OrganizationCookieDto,
} from "../../../common/service/server/dto/CookieScanDto";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { Callout, CalloutVariant } from "../Callout";
import { fmtCookieCategory } from "../../util/cookie";
import { Error as InputError } from "../../copy/input";
import { FIRST_PARTY_RECIPIENT_UUID } from "../organisms/cookies/CookieScanner";

const CATEGORY_OPTIONS: CookieCategory[] = [
  "ANALYTICS",
  "FUNCTIONALITY",
  "ADVERTISING",
  "PERSONALIZATION",
  "SECURITY",
];

/**
 * Mirrors CookieScannerService.createCustomCookie:
 * uniqueness is (organization, trimmed name, trimmed domain) against any existing cookie.
 */
const findDuplicateCookie = (
  name: string | undefined,
  domain: string | undefined,
  existingCookies: OrganizationCookieDto[],
): OrganizationCookieDto | undefined => {
  const trimmedName = name?.trim();
  const trimmedDomain = domain?.trim();
  if (!trimmedName || !trimmedDomain) {
    return undefined;
  }
  return existingCookies.find((c) => c.name === trimmedName && c.domain === trimmedDomain);
};

const duplicateCookieMessage = (name: string, domain: string) =>
  `Cookie '${name.trim()}' already exists for domain '${domain.trim()}'.`;

type FormValues = {
  name?: string;
  domain?: string;
  duration?: number;
  durationUnit?: string;
  httpOnly?: boolean;
  secure?: boolean;
  categories?: CookieCategory[];
  vendorId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CustomCookieDto) => Promise<void>;
  recipients: OrganizationDataRecipientDto[];
  existingCookies: OrganizationCookieDto[];
};

export const CustomCookieDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  recipients,
  existingCookies,
}) => {
  const [error, setError] = useState(false);

  const categoryOptions = useMemo(
    () => CATEGORY_OPTIONS.map((c) => ({ value: c, label: fmtCookieCategory(c) })),
    [],
  );

  const recipientOptions = useMemo(
    () => [
      { value: FIRST_PARTY_RECIPIENT_UUID, label: "First Party", divider: true },
      ...recipients.map((r) => ({ value: r.vendorId, label: r.name })),
    ],
    [recipients],
  );

  const createCustomCookieValidation = (values: FormValues) => {
    const errors = {} as Record<string, string>;

    requiredString(values, "name", errors);
    requiredUrl(values, "domain", errors);
    if (values.durationUnit && values.durationUnit != "session") {
      requiredNumber(values, "duration", errors);
    }
    if (!values.categories || values.categories.length === 0) {
      errors.categories = InputError.Required;
    }
    requiredString(values, "vendorId", errors);

    if (findDuplicateCookie(values.name, values.domain, existingCookies)) {
      errors.name = InputError.MustBeUnique;
      errors.domain = InputError.MustBeUnique;
    }

    return errors;
  };

  return (
    <HelpDialog
      open={open}
      onClose={onClose}
      title={"Add Cookie"}
      className="intro-help-dialog"
      maxWidth="md"
    >
      <Form
        mutators={{ setFieldData }}
        onSubmit={async (data: FormValues) => {
          if (findDuplicateCookie(data.name, data.domain, existingCookies)) {
            return;
          }
          setError(false);
          try {
            const isFirstParty = data.vendorId === FIRST_PARTY_RECIPIENT_UUID;
            await onSubmit({
              name: data.name!,
              domain: data.domain!,
              duration: data.duration,
              durationUnit: data.durationUnit,
              httpOnly: data.httpOnly,
              secure: data.secure,
              categories: data.categories ?? [],
              firstParty: isFirstParty,
              vendorId: isFirstParty ? undefined : data.vendorId,
            });
          } catch (e) {
            setError(true);
          }
        }}
        validate={createCustomCookieValidation}
        initialValues={{
          durationUnit: "session",
          categories: [],
        }}
        render={({ handleSubmit, values, errors }) => {
          const duplicate = findDuplicateCookie(values.name, values.domain, existingCookies);
          let isDisabled = Object.keys(errors).length > 0 || Boolean(duplicate);
          if (
            !values.name ||
            !values.domain ||
            (values.durationUnit != "session" && !values.duration) ||
            !values.categories?.length ||
            !values.vendorId
          ) {
            isDisabled = true;
          }
          return (
            <form onSubmit={handleSubmit}>
              <div className="flex-row flex--expand">
                <Text field="name" label="Name" required />
              </div>
              <div className="flex-row flex--expand">
                <Text field="domain" label="Domain" required />
              </div>
              <div className="flex-row flex--expand">
                <Select
                  field="categories"
                  label="Type"
                  required
                  multiple
                  showChecks
                  options={categoryOptions}
                  formControlProps={{ fullWidth: true }}
                  renderValue={(selected: unknown) => (
                    <MultiSelectChips
                      selectedValues={(selected as CookieCategory[]).map(fmtCookieCategory)}
                    />
                  )}
                />
              </div>
              <div className="flex-row flex--expand">
                <Select
                  field="vendorId"
                  label="Data Recipient"
                  required
                  options={recipientOptions}
                  formControlProps={{ fullWidth: true }}
                />
              </div>
              <div className="flex-row">
                <Text
                  type="number"
                  field="duration"
                  label="Duration"
                  helperText={errors.duration ? "Required" : false}
                  disabled={!values.durationUnit || values.durationUnit == "session"}
                  required={values.durationUnit && values.durationUnit != "session"}
                  fullWidth={false}
                  className="w-128"
                />
                <Select
                  label="Unit"
                  field="durationUnit"
                  required
                  value="session"
                  fullWidth={false}
                  className="w-192"
                  options={[
                    {
                      label: "Session",
                      value: "session",
                      divider: true,
                    },
                    {
                      label: "Minutes",
                      value: "minutes",
                    },
                    {
                      label: "Hours",
                      value: "hours",
                    },
                    {
                      label: "Days",
                      value: "days",
                    },
                    {
                      label: "Months",
                      value: "months",
                    },
                    {
                      label: "Years",
                      value: "years",
                    },
                  ]}
                />
              </div>
              <div className="flex-row mt-md">
                <Checkbox field="httpOnly" label="HTTP-Only" />
                <Checkbox field="secure" label="Secure" />
              </div>
              {duplicate && (
                <Callout className="my-md" variant={CalloutVariant.Red}>
                  {duplicateCookieMessage(values.name!, values.domain!)}
                </Callout>
              )}
              {error && !duplicate && (
                <Callout className="my-md" variant={CalloutVariant.Red}>
                  Something went wrong, please try again later.
                </Callout>
              )}
              <div className="text-right mt-md">
                <Button
                  className="ml-md w-192"
                  variant="contained"
                  color="default"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isDisabled}
                  type="submit"
                  className="ml-md w-192"
                  variant="contained"
                  color="primary"
                >
                  Save
                </Button>
              </div>
            </form>
          );
        }}
      />
    </HelpDialog>
  );
};
